/**
 * Payment Validation API
 * PAYG (Pay-As-You-Go) validation functions
 */

import { browserContext, getClientPlatformForAPI } from '../utils/platform';
import { apiRequest } from './core';

// ============================================================================
// Types
// ============================================================================

/**
 * Payment validation state enum (API returns 'state' field)
 * - 'pending': validation in progress, keep polling
 * - 'validated': validation successful
 * - 'failed': validation failed
 */
export type PaymentValidationState = 'pending' | 'validated' | 'failed';

/**
 * Payment validation response from API
 */
export interface PaymentValidationResponse {
  id: number;
  state: PaymentValidationState;
  error_message?: string;
  ok_message?: string;
}

export interface CreateValidationParams {
  advisorId?: number;
  context?: string;
  orderType?: string;
  rcToken?: string;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Create a payment validation (PAYG mode)
 * Validates payment method WITHOUT charging - used for "Zen Mode"
 *
 * POST /my/payment_validations
 *
 * @param paymentSourceId - The payment source to validate
 * @param params - Additional parameters (advisor_id, context, etc.)
 */
export const createPaymentValidation = async (
  paymentSourceId: number,
  params?: CreateValidationParams
): Promise<PaymentValidationResponse> => {
  const requestBody = {
    payment_source_id: paymentSourceId,
    // Use intended_advisor_id (matches old implementation)
    ...(params?.advisorId && { intended_advisor_id: params.advisorId }),
    ...(params?.context && { context: params.context }),
    ...(params?.orderType && { order_type: params.orderType }),
    ...(params?.rcToken && { rc_token: params.rcToken }),
    platform: getClientPlatformForAPI(),
    ...browserContext
  };

  return apiRequest<PaymentValidationResponse>(
    '/my/payment_validations',
    {
      method: 'POST',
      body: requestBody
    }
  );
};

/**
 * Get payment validation status (polling)
 *
 * GET /my/payment_validations/{id}
 *
 * @param validationId - The validation ID to check
 */
export const getPaymentValidation = async (
  validationId: number
): Promise<PaymentValidationResponse> => apiRequest<PaymentValidationResponse>(
  `/my/payment_validations/${ validationId }`
);
